/**
 * @fileoverview Goals on the instructor's student profile (F6 / Spec 07).
 *
 * The headline of a goal is a STAGE the teacher sets, not a percentage — see
 * `routes/goals.py` for why. So the control here is a four-step picker and the
 * activity rollup is a quiet line underneath it, which is the honest ordering:
 * the teacher's judgement first, the evidence second.
 *
 * The five-goal cap is the pedagogy rather than a technical limit, so the
 * server's refusal is rendered IN PLACE (the same treatment the roster cap
 * gets) instead of a toast that vanishes — an instructor who has hit it needs
 * to read what to do about it, not be told off.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import { GOALS_ENABLED } from "@/lib/featureFlags";
import { Card, SectionHeader, Button, ListRow, Tag } from "@/components/studio/primitives";

const STAGES = [
  { key: "not_started", label: "Not started" },
  { key: "working", label: "Working on it" },
  { key: "polishing", label: "Polishing" },
  { key: "achieved", label: "Achieved" },
];

const CATEGORIES = ["repertoire", "technique", "exam", "performance", "habit", "other"];

const FIELD =
  "w-full rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2 font-sans text-sm text-[var(--text-primary)]";

export function StudentGoalsCard({ studentId }) {
  const [goals, setGoals] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", category: "repertoire", target_date: "" });
  const [capMessage, setCapMessage] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/students/${studentId}/goals?status=active`);
      setGoals(data.goals || []);
    } catch {
      /* A goals card is not worth an error toast on a profile page. */
    }
  }, [studentId]);

  useEffect(() => {
    if (GOALS_ENABLED) load();
  }, [load]);

  if (!GOALS_ENABLED) return null;

  const create = async () => {
    setBusy(true);
    setCapMessage(null);
    try {
      await api.post(`/students/${studentId}/goals`, {
        title: form.title,
        category: form.category,
        target_date: form.target_date || undefined,
      });
      setForm({ title: "", category: "repertoire", target_date: "" });
      setAdding(false);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "goal_limit_reached") setCapMessage(detail.message);
      else toast.error(getErrorMessage(err, "Couldn't save that goal"));
    } finally {
      setBusy(false);
    }
  };

  const setStage = async (goal, stage) => {
    try {
      await api.patch(`/goals/${goal.goal_id}`, { stage });
      if (stage === "achieved") toast.success(`${goal.title} — achieved`);
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't update that goal"));
    }
  };

  const archive = async (goal) => {
    try {
      await api.post(`/goals/${goal.goal_id}/archive`, {});
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't archive that goal"));
    }
  };

  return (
    <Card data-testid="student-goals">
      <SectionHeader action={adding ? "Cancel" : "Add goal"} onAction={() => setAdding((v) => !v)} actionTestId="goal-add">
        Goals
      </SectionHeader>

      {goals.length === 0 && !adding ? (
        <p className="font-sans text-[14px] text-[var(--text-tertiary)]" data-testid="goals-empty">
          No goals yet. A recital piece, a grade, a technique to crack — anything the practising is heading toward.
        </p>
      ) : null}

      <div className="-mx-[18px]">
        {goals.map((goal, i) => (
          <ListRow
            key={goal.goal_id}
            borderBottom={i < goals.length - 1}
            title={goal.title}
            subtitle={
              goal.rollup
                ? `${goal.rollup.assignments_open} open · ${goal.rollup.assignments_completed} done` +
                  (goal.target_date ? ` · by ${goal.target_date}` : "")
                : goal.target_date
                  ? `by ${goal.target_date}`
                  : null
            }
            trailing={
              <div className="flex items-center gap-2">
                <Tag>{goal.category}</Tag>
                <select
                  value={goal.stage}
                  onChange={(e) => setStage(goal, e.target.value)}
                  data-testid={`goal-stage-${goal.goal_id}`}
                  className="rounded-lg border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-2 py-1 font-sans text-[13px] text-[var(--text-primary)]"
                >
                  {STAGES.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => archive(goal)}
                  data-testid={`goal-archive-${goal.goal_id}`}
                  className="font-sans text-[13px] text-[var(--text-tertiary)] hover:underline"
                >
                  Archive
                </button>
              </div>
            }
          />
        ))}
      </div>

      {capMessage ? (
        <p className="mt-4 font-sans text-[13px] text-[var(--text-secondary)]" data-testid="goal-cap">
          {capMessage}
        </p>
      ) : null}

      {adding ? (
        <div className="mt-4 flex flex-col gap-3">
          <input
            className={FIELD}
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Moonlight Sonata, first movement"
            data-testid="goal-title"
          />
          <div className="flex gap-3">
            <select
              className={FIELD}
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              data-testid="goal-category"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              type="date"
              className={FIELD}
              value={form.target_date}
              onChange={(e) => setForm((f) => ({ ...f, target_date: e.target.value }))}
              data-testid="goal-date"
            />
          </div>
          <div>
            <Button onPress={create} disabled={busy || !form.title.trim()} data-testid="goal-save">
              Save goal
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

export default StudentGoalsCard;
